import React, { useEffect, useRef, useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import type { TabConfig } from './TabbedDialog';

interface TabbedScrollPaneProps {
  tabs: TabConfig[];
  defaultTab?: string;
  /** Called when the user navigates between sections (via click or scroll) */
  onActiveTabChange?: (key: string) => void;
  /**
   * When true, render only the scrollable content column — no left nav, no
   * scrollspy. Useful for narrower chromes (e.g. drawers) where a sidebar
   * crowds the content.
   */
  hideNav?: boolean;
}

const useStyles = makeStyles({
  body: {
    display: 'flex',
    height: '100%',
    minHeight: 0,
  },
  navTree: {
    width: '200px',
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
    flexShrink: 0,
    overflowY: 'auto',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('8px', '12px'),
    cursor: 'pointer',
    ...shorthands.borderRadius('4px'),
    fontSize: '14px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground2,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontWeight: 600,
  },
  contentArea: {
    flex: 1,
    overflowY: 'auto',
    ...shorthands.padding('16px', '24px'),
    minHeight: 0,
    scrollBehavior: 'smooth',
  },
  scrollSection: {
    scrollMarginTop: '16px',
  },
  scrollSectionDivided: {
    scrollMarginTop: '16px',
    marginTop: '32px',
    paddingTop: '32px',
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  scrollEndSpacer: {
    height: '50vh',
  },
});

/**
 * TabbedScrollPane - left nav + long-scroll content with scrollspy
 *
 * Renders all tabs in a single scrollable column. The left nav reflects the
 * section currently in view (via IntersectionObserver) and clicking a nav item
 * smooth-scrolls to that section. Designed to be used inside any chrome
 * (dialog body, drawer body, or page content).
 */
export const TabbedScrollPane: React.FC<TabbedScrollPaneProps> = ({
  tabs,
  defaultTab,
  onActiveTabChange,
  hideNav = false,
}) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.key || '');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (hideNav) return; // no scrollspy needed when nav is hidden
    const container = scrollContainerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const topMost = visible.reduce((a, b) =>
          a.boundingClientRect.top < b.boundingClientRect.top ? a : b
        );
        const id = (topMost.target as HTMLElement).dataset.sectionId;
        if (id) {
          setActiveTab(id);
          onActiveTabChange?.(id);
        }
      },
      {
        root: container,
        rootMargin: '0px 0px -60% 0px',
        threshold: 0,
      }
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [tabs, onActiveTabChange, hideNav]);

  const handleNavClick = (key: string) => {
    const el = sectionRefs.current[key];
    if (el) {
      setActiveTab(key);
      onActiveTabChange?.(key);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sectionsContent = (
    <div className={styles.contentArea} ref={scrollContainerRef}>
      {tabs.map((tab, index) => (
        <div
          key={tab.key}
          ref={(el) => { sectionRefs.current[tab.key] = el; }}
          data-section-id={tab.key}
          className={index === 0 ? styles.scrollSection : styles.scrollSectionDivided}
        >
          {tab.content}
        </div>
      ))}
      {/* Bottom spacer only matters when nav is visible — it ensures clicking
          the last nav item scrolls that section to the top. Without nav
          (drawer mode), the spacer is just dead whitespace. */}
      {!hideNav && <div className={styles.scrollEndSpacer} aria-hidden="true" />}
    </div>
  );

  if (hideNav) {
    return sectionsContent;
  }

  return (
    <div className={styles.body}>
      <div className={styles.navTree}>
        {tabs.map((tab) => (
          <div
            key={tab.key}
            className={`${styles.navItem} ${activeTab === tab.key ? styles.navItemActive : ''}`}
            onClick={() => handleNavClick(tab.key)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </div>
        ))}
      </div>
      {sectionsContent}
    </div>
  );
};

export default TabbedScrollPane;
